import { Tag, ListboxGroup, Button, CheckboxGroup } from '@/common'
import { CircleXIcon } from '@/common/components/icons'
import useI18n from '@/shared/hooks/usei18n'
import {
  FilterType,
  FilterValue,
  ToggleFilters,
  FILTER_NONE_VALUE,
  SINGLE_SELECT_FILTER_TYPES
} from '../../../hooks/useImportedGoodsFilters'

type FilterBarProps = {
  filterOptions: {
    aggregatedGoodsCategories: { value: string; label: string }[]
    cnCodes: { value: string; label: string }[]
    suppliers: { value: string; label: string }[]
    installations: { value: string; label: string }[]
    emissionsReports: { value: string; label: string }[]
    countries: { value: string; label: string }[]
    importers: { value: string; label: string }[]
    sellers: { value: string; label: string }[]
    grrFilenames: { value: string; label: string }[]
    quarters: { value: string; label: string }[]
  }
  activeFilters: FilterValue[]
  onAddFilter: (filter: FilterValue) => void
  onRemoveFilter: (filter: FilterValue) => void
  onClearAll: () => void
  toggleFilters: ToggleFilters
  onToggleDefaultValues: (value: boolean) => void
  onToggleIncomplete: (value: boolean) => void
  isRepresentative: boolean
}

type FilterConfig = {
  type: FilterType
  /** Key under `cbamReporting.json` → `filters.*` */
  labelKey: string
  optionsKey: keyof FilterBarProps['filterOptions']
  filterWidthClass: string
}

const CBAM_REPORTING_NS = 'cbamReporting'

const filterConfigs: FilterConfig[] = [
  {
    type: 'aggregatedGoodsCategory',
    labelKey: 'filters.aggregatedGoodsCategory',
    optionsKey: 'aggregatedGoodsCategories',
    filterWidthClass: 'min-w-48'
  },
  {
    type: 'cnCode',
    labelKey: 'filters.cnCode',
    optionsKey: 'cnCodes',
    filterWidthClass: 'min-w-64'
  },
  {
    type: 'supplier',
    labelKey: 'filters.supplier',
    optionsKey: 'suppliers',
    filterWidthClass: 'min-w-40'
  },
  {
    type: 'installation',
    labelKey: 'filters.installation',
    optionsKey: 'installations',
    filterWidthClass: 'min-w-40'
  },
  {
    type: 'emissionsReport',
    labelKey: 'filters.emissionsReport',
    optionsKey: 'emissionsReports',
    filterWidthClass: 'min-w-52'
  },
  {
    type: 'country',
    labelKey: 'filters.country',
    optionsKey: 'countries',
    filterWidthClass: 'min-w-36'
  },
  {
    type: 'importer',
    labelKey: 'filters.importer',
    optionsKey: 'importers',
    filterWidthClass: 'min-w-40'
  },
  {
    type: 'seller',
    labelKey: 'filters.seller',
    optionsKey: 'sellers',
    filterWidthClass: 'min-w-40'
  },
  {
    type: 'grrFilename',
    labelKey: 'filters.grrFilename',
    optionsKey: 'grrFilenames',
    filterWidthClass: 'min-w-56'
  },
  {
    type: 'quarter',
    labelKey: 'filters.quarterLabel',
    optionsKey: 'quarters',
    filterWidthClass: 'min-w-36'
  }
]

const filterNoneLabelKeys: Record<FilterType, string | null> = {
  supplier: 'filters.noSupplier',
  installation: 'filters.noInstallation',
  emissionsReport: 'filters.noEmissionsReport',
  importer: 'filters.noImporter',
  seller: 'filters.noSeller',
  grrFilename: 'filters.noGrrFilename',
  aggregatedGoodsCategory: null,
  cnCode: null,
  country: null,
  quarter: null
}

function FilterDropdown({
  label,
  options,
  type,
  onSelect,
  activeValues,
  selectedValue = '',
  className
}: {
  label: string
  options: { value: string; label: string }[]
  type: FilterType
  onSelect: (filter: FilterValue) => void
  activeValues: string[]
  selectedValue?: string
  className?: string
}) {
  const { t } = useI18n()
  const singleSelect = SINGLE_SELECT_FILTER_TYPES.has(type)

  // Mark active options as disabled and translate None options
  const optionsWithDisabled = options.map(opt => {
    const translationKey =
      opt.value === FILTER_NONE_VALUE ? filterNoneLabelKeys[type] : null
    return {
      ...opt,
      label: translationKey ? t(translationKey, { ns: CBAM_REPORTING_NS }) : opt.label,
      disabled: singleSelect ? false : activeValues.includes(opt.value)
    }
  })

  return (
    <div className={className}>
      <ListboxGroup
        label=""
        value={singleSelect ? selectedValue : ''}
        placeholder={label}
        options={optionsWithDisabled}
        onChange={value => {
          const selected = options.find(opt => opt.value === value)
          if (!selected) {
            return
          }
          if (!singleSelect && activeValues.includes(value)) {
            return
          }
          const next: FilterValue = { type, value: selected.value, label: selected.label }
          onSelect(next)
        }}
      />
    </div>
  )
}

function FilterTag({ filter, onRemove }: { filter: FilterValue; onRemove: () => void }) {
  const { t } = useI18n()

  const noneLabelKey = filterNoneLabelKeys[filter.type]

  const displayLabel =
    filter.value === FILTER_NONE_VALUE && noneLabelKey
      ? t(noneLabelKey, { ns: CBAM_REPORTING_NS })
      : filter.label

  return (
    <button
      type="button"
      onClick={onRemove}
      className="group cursor-pointer"
      aria-label={`Remove filter ${displayLabel}`}
    >
      <Tag
        title={displayLabel}
        label={
          <span className="flex items-center gap-1.5">
            <span className="truncate max-w-48">{displayLabel}</span>
            <CircleXIcon className="size-3.5 opacity-50 group-hover:opacity-100 transition-opacity shrink-0" />
          </span>
        }
      />
    </button>
  )
}

export default function ImportedGoodsFilterBar({
  filterOptions,
  activeFilters,
  onAddFilter,
  onRemoveFilter,
  onClearAll,
  toggleFilters,
  onToggleDefaultValues,
  onToggleIncomplete,
  isRepresentative
}: FilterBarProps) {
  const { t } = useI18n()

  const visibleFilterConfigs = isRepresentative
    ? filterConfigs
    : filterConfigs.filter(config => config.type !== 'importer')
  const visibleActiveFilters = isRepresentative
    ? activeFilters
    : activeFilters.filter(filter => filter.type !== 'importer')

  const hasActiveFilters =
    visibleActiveFilters.length > 0 ||
    toggleFilters.showOnlyDefaultValues ||
    toggleFilters.showOnlyIncomplete

  return (
    <div className="flex flex-col gap-3 mb-4">
      {/* Filter Dropdowns - Grid Layout */}
      <div className="flex flex-col gap-2">
        <span className="text-sm text-text-secondary">{t('cbamReporting:filters.filterBy')}:</span>
        <div className="flex flex-wrap gap-2">
          {visibleFilterConfigs.map(config => {
            const filtersOfType = visibleActiveFilters.filter(
              (filterItem): filterItem is FilterValue => filterItem.type === config.type
            )
            const singleSelect = SINGLE_SELECT_FILTER_TYPES.has(config.type)

            return (
              <div key={config.type} className="flex-1">
                <FilterDropdown
                  label={t(config.labelKey, { ns: CBAM_REPORTING_NS })}
                  options={filterOptions[config.optionsKey]}
                  type={config.type}
                  onSelect={onAddFilter}
                  activeValues={filtersOfType.map(filterItem => filterItem.value)}
                  selectedValue={
                    singleSelect ? (filtersOfType[0]?.value ?? '') : ''
                  }
                  className={config.filterWidthClass}
                />
              </div>
            )
          })}
        </div>
      </div>

      {/* Toggle Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <CheckboxGroup
          id="filter-default-values"
          label={t('cbamReporting:filters.showOnlyDefaultValues')}
          checked={toggleFilters.showOnlyDefaultValues}
          onChange={onToggleDefaultValues}
        />
        <CheckboxGroup
          id="filter-incomplete"
          label={t('cbamReporting:filters.showOnlyIncomplete')}
          checked={toggleFilters.showOnlyIncomplete}
          onChange={onToggleIncomplete}
        />
      </div>

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-text-secondary">
            {t('cbamReporting:filters.activeFilters')}:
          </span>
          {toggleFilters.showOnlyDefaultValues && (
            <FilterTag
              filter={{
                type: 'cnCode',
                value: '__toggle_default__',
                label: t('cbamReporting:filters.showOnlyDefaultValues')
              }}
              onRemove={() => onToggleDefaultValues(false)}
            />
          )}
          {toggleFilters.showOnlyIncomplete && (
            <FilterTag
              filter={{
                type: 'cnCode',
                value: '__toggle_incomplete__',
                label: t('cbamReporting:filters.showOnlyIncomplete')
              }}
              onRemove={() => onToggleIncomplete(false)}
            />
          )}
          {visibleActiveFilters.map((filter, index) => (
            <FilterTag
              key={`${filter.type}-${filter.value}-${index}`}
              filter={filter}
              onRemove={() => onRemoveFilter(filter)}
            />
          ))}
          <Button variant="txt-default" onClick={onClearAll}>
            {t('cbamReporting:filters.clearAll')}
          </Button>
        </div>
      )}
    </div>
  )
}
